<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Historique des versions"
      @mousedown.self="emit('close')"
    >
      <div class="bg-card w-full max-w-md h-full flex flex-col shadow-2xl border-l border-border transform transition-transform duration-300">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 class="text-sm font-black uppercase tracking-widest text-foreground flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>
            Historique Global du Budget
          </h2>
          <button
            class="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            @click="emit('close')"
            aria-label="Fermer"
          >×</button>
        </div>

        <!-- Create Manual Snapshot -->
        <div class="p-6 border-b border-border bg-muted/20 space-y-3">
          <p class="text-xs font-bold uppercase text-muted-foreground tracking-wider">Créer une sauvegarde manuelle</p>
          <div class="flex gap-2">
            <input
              v-model="newSnapshotName"
              type="text"
              placeholder="Ex: Avant changement de statuts"
              class="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:ring-2 focus:ring-primary outline-none"
              @keyup.enter="handleCreate"
            />
            <button
              class="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 transition disabled:opacity-50"
              :disabled="!newSnapshotName.trim() || saving"
              @click="handleCreate"
            >
              Créer
            </button>
          </div>
        </div>

        <!-- List -->
        <div class="flex-1 overflow-y-auto p-4 space-y-2">
          <div v-if="snapshots.length === 0" class="text-center py-10 text-muted-foreground">
            <p class="text-sm">Aucun historique disponible.</p>
          </div>
          
          <button
            v-for="snap in snapshots"
            :key="snap.id"
            class="w-full text-left p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/30 transition-all flex flex-col gap-1.5"
            :class="{ 'ring-2 ring-primary bg-primary/5': currentSnapshot?.id === snap.id }"
            @click="handleLoad(snap.id)"
          >
            <div class="flex items-center justify-between">
              <span class="text-sm font-bold text-foreground">
                {{ snap.name || 'Sauvegarde Automatique' }}
              </span>
              <span class="text-[10px] uppercase font-black px-2 py-0.5 rounded-full"
                    :class="snap.isAuto ? 'bg-muted text-muted-foreground' : 'bg-emerald-500/15 text-emerald-500'">
                {{ snap.isAuto ? 'Auto' : 'Manuel' }}
              </span>
            </div>
            <p class="text-xs text-muted-foreground">
              {{ new Date(snap.updatedAt || snap.createdAt).toLocaleString() }}
            </p>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useBudget } from '../../../composables/useBudget'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const { snapshots, currentSnapshot, createManualSnapshot, loadSnapshot } = useBudget()

const newSnapshotName = ref('')
const saving = ref(false)

async function handleCreate() {
  if (!newSnapshotName.value.trim() || saving.value) return
  saving.value = true
  try {
    await createManualSnapshot(newSnapshotName.value.trim())
    newSnapshotName.value = ''
  } finally {
    saving.value = false
  }
}

async function handleLoad(id: number) {
  await loadSnapshot(id)
  emit('close')
}
</script>
